import { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  addNewCourseLessonApi,
  getLessonsLevels,
  getLessonTypes,
  updateLessonInCourseApi,
} from "../../apis";

import DropdownForm from "../Form/DropdownForm";
import FormField from "../Form/FormField";
import TextareaForm from "../Form/TextareaForm";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import RowLoader from "../UI/RowLoader";
import {
  ARABIC_REGEX,
  ENGLISH_REGEX,
  IS_ACTIVE,
} from "../../constants/globals";

const AddOrEditLesson = ({ mode = "add", initialValues = {} }) => {
  const navigate = useNavigate();
  const { courseId, lessonId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [lessonsTypeData, setLessonsTypeData] = useState([]);
  const [lessonsLevels, setLessonsLevel] = useState([]);
  const handleLessonsTypeDropdown = lessonsTypeData.map((le) => ({
    id: le,
    title: le,
  }));
  const handleLessonsLevels = lessonsLevels.map((le) => ({
    id: le,
    title: le,
  }));

  const defaultValues = {
    lesson_name_english: "",
    lesson_name_arabic: "",
    lesson_description_english: "",
    lesson_description_arabic: "",
    lesson_type: "",
    level: "",
    is_active: "1",
    duration: "",
    video_link: "",
    confirm: false,
  };
  // Ensure initialValues always has default values
  const formInitialValues = useMemo(
    () => ({
      ...defaultValues,
      ...initialValues, // Override defaults if values exist
    }),
    [initialValues]
  );

  // Fetch Data Once When Component Mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lessons, lessonsLevels] = await Promise.all([
          getLessonTypes(),
          getLessonsLevels(),
        ]);
        setLessonsTypeData(lessons.data || []);
        setLessonsLevel(lessonsLevels.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  // Form Validation Schema
  const validationSchema = Yup.object().shape({
    lesson_name_english: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    lesson_name_arabic: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    lesson_description_english: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    lesson_description_arabic: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    lesson_type: Yup.string().required("Required"),
    level: Yup.string().required("Required"),
    is_active: Yup.string().required("Required"),
    duration: Yup.number()
      .typeError("Lesson duration must be a number")
      .required("Lesson duration is required"),
    confirm: Yup.boolean().oneOf([true], "You must confirm your choices"),
    video_link: Yup.string().required("Required"),
  });

  const handleFormSubmit = async (data, { resetForm }) => {
    const handleData = { ...data };
    setIsLoading(true);
    try {
      delete handleData.confirm;
      if (mode === "edit") {
        if (
          JSON.stringify({ ...initialValues, confirm: true }) ===
          JSON.stringify(data)
        ) {
          Swal.fire(
            "No changes detected",
            "No changes made to the lesson data",
            "info"
          );
          return;
        } else {
          await updateLessonInCourseApi(courseId, lessonId, handleData);
        }
      } else {
        await addNewCourseLessonApi(courseId, handleData);
      }
      Swal.fire("Success", "course lesson submitted successfully!", "success");
      resetForm();
      if (mode === "edit") {
        navigate(-1);
      }
    } catch (error) {
      console.error("API Error:", error);
      Swal.fire(
        "Sorry!",
        error.response.data.message?.join(" , ") ||
          "Error while submitted new lesson",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <p className="tw-text-[#BF0404] tw-text-end tw-mb-1">
        * means this field is required
      </p>
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleChange,
          handleBlur,
          values,
          setFieldValue,
        }) => (
          <Form className="card !tw-rounded-xl">
            <div className="card-body tw-shadow-xl tw-rounded-xl">
              <div className="row">
                {/*  Title English */}
                <FormField
                  name="lesson_name_english"
                  label="Lesson Title English"
                  placeholder="Lesson Title English"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.lesson_name_english}
                />

                {/*  Title Arabic */}
                <FormField
                  name="lesson_name_arabic"
                  label="Lesson Title Arabic"
                  placeholder="Lesson Title Arabic"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.lesson_name_arabic}
                />

                {/* Lesson Description English  */}
                <TextareaForm
                  name="lesson_description_english"
                  placeholder="Lesson Description English "
                  touched={touched.lesson_description_english}
                  errors={errors.lesson_description_english}
                  handleBlur={handleBlur}
                  label="Lesson Description English "
                />

                {/* Lesson Description Arabic */}
                <TextareaForm
                  name="lesson_description_arabic"
                  placeholder="Lesson Description Arabic"
                  touched={touched.lesson_description_arabic}
                  errors={errors.lesson_description_arabic}
                  handleBlur={handleBlur}
                  label="Lesson Description Arabic"
                />

                {/* lessonType */}
                <DropdownForm
                  handleChange={handleChange}
                  name="lesson_type"
                  dataToDrop={handleLessonsTypeDropdown}
                  error={errors.lesson_type}
                  touched={touched.lesson_type}
                  label="Lesson Type"
                  placeholder="Lesson Type"
                  gridStyle="col-sm-6"
                />

                {/* lessonLevel */}
                <DropdownForm
                  handleChange={handleChange}
                  name="level"
                  dataToDrop={handleLessonsLevels}
                  error={errors.level}
                  touched={touched.level}
                  label="Lesson Level"
                  placeholder="Lesson Level"
                  gridStyle="col-sm-6"
                />

                {/*is_active */}
                <DropdownForm
                  handleChange={handleChange}
                  name="is_active"
                  dataToDrop={IS_ACTIVE}
                  error={errors.is_active}
                  touched={touched.is_active}
                  label="is Active"
                  placeholder="Is Active"
                  gridStyle="col-sm-6"
                />

                {/* Duration */}
                <FormField
                  type="number"
                  name="duration"
                  label="Lesson Duration ( In minutes )"
                  placeholder="Lesson Duration"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.duration}
                />

                {/* LessonVideoLink */}
                <TextareaForm
                  required
                  name="video_link"
                  placeholder="Lesson Video Link"
                  touched={touched.video_link}
                  errors={errors.video_link}
                  handleBlur={handleBlur}
                  label="Lesson Video Link"
                />
                {/* confirm */}
                <div className="col-md-12 tw-flex tw-items-center">
                  <input
                    type="checkbox"
                    name="confirm"
                    id="confirm"
                    checked={values.confirm}
                    onChange={handleChange}
                    className="!tw-mr-2 tw-w-4 "
                  />
                  <label
                    htmlFor="confirm"
                    className="tw-text-sm tw-cursor-pointer !tw-m-0 !tw-mt-1"
                  >
                    I confirm that, the Lesson data is correct.
                  </label>
                </div>
                {errors.confirm && touched.confirm && (
                  <p className="tw-text-red-500 tw-text-xs tw-mt-1">
                    {errors.confirm}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="tw-mt-4 tw-text-right">
                <button
                  type="submit"
                  className="!tw-bg-primary btn tw-text-white !tw-w-[100px] hover:!tw-bg-primary/90 hover:tw-text-white"
                >
                  {isLoading ? <RowLoader paddingStyle="tw-py-0" /> : "Submit"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddOrEditLesson;
